import React, { useEffect, useState } from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import {
  getInstallersForWorkDays,
  getWorkDays,
  getWorkMonths,
  getWorkWeeks,
  InstallationTimeField,
  toWorkDays,
} from 'services/offers/workDays';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import getFormStateHandler from 'services/forms/getFormStateHandler';
import InputLabel from 'components/layout/forms/InputLabel';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';
import { CONSIGNMENT, H } from 'services/enums/units';

import { useCurrency } from 'services/offers/currency';
import getVersionInstallationContract from './getVersionInstallationContract';
import IVersion from '../IVersion';

// At most two decimals without trailing zeros, with a decimal comma (e.g. 2 or 2,5)
const formatShortNumber = (value: number): string =>
  String(Math.round(value * 100) / 100).replace('.', ',');

const InstallationContract = ({
  locked,
  productUnit,
  quantity,
  setVersion,
  version,
}: {
  locked: boolean;
  productUnit: string;
  quantity: number;
  setVersion: (version: IVersion) => void;
  version: IVersion;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const currency = useCurrency();

  const handleFormState = getFormStateHandler(version, setVersion);

  const offerIsLocked = () => locked;

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  const installationContract = getVersionInstallationContract(
    version,
    quantity,
    installationCategories
  );

  /*
   * Installers and installation time (8 h a day, 5 days a week, 21 days a month). KTA gives
   * the total hours and the installers share them into work days, weeks and months. Typing
   * the work days, weeks or months changes the installers, the hours stay. The typed text is
   * kept as it is until the KTA, the installers or another time is changed. The installers
   * are saved with the version.
   */
  const installers = version.installerCount ?? '1';
  const setInstallers = (installerCount: string) =>
    setVersion({ ...version, installerCount });
  const [typedTime, setTypedTime] = useState<{
    field: InstallationTimeField;
    text: string;
  } | null>(null);

  // A time typed for other hours or for another alternative is not shown any more
  useEffect(() => {
    setTypedTime(null);
  }, [installationContract.totalHours, version.id, version.name]);

  const workDays = getWorkDays(
    installationContract.totalHours,
    numericStringToFloat(installers)
  );

  // A batch (erä) is one whole: the contract per unit equals the whole contract and
  // units per hour say nothing, so those are not shown
  const showPerUnit = productUnit !== CONSIGNMENT;

  // The other times follow the time typed by the user
  const shownWorkDays =
    typedTime === null
      ? workDays
      : toWorkDays(typedTime.field, numericStringToFloat(typedTime.text));

  const getTimeValue = (field: InstallationTimeField, value: number) =>
    typedTime !== null && typedTime.field === field
      ? typedTime.text
      : formatGroupedNumber(value);

  const handleAverageHourlyEarnings = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTypedTime(null);
    handleFormState(event);
  };

  const handleInstallers = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTypedTime(null);
    setInstallers(event.target.value);
  };

  const getTimeHandler =
    (field: InstallationTimeField) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setTypedTime({ field, text: value });

      const installersForTime = getInstallersForWorkDays(
        installationContract.totalHours,
        toWorkDays(field, numericStringToFloat(value))
      );

      if (installersForTime > 0) {
        setInstallers(formatShortNumber(installersForTime));
      }
    };

  return (
    <div className="calculation-sum-row">
      <h2>{t(`${translations.calculationWindow}:Installation contract`)}</h2>
      <Row>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Contract price`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Installation contract`)}{' '}
            {currency}
          </InputLabel>
        </Col>
        {showPerUnit ? (
          <Col>
            <InputLabel>
              {`${t(
                `${translations.calculationWindow}:Installation contract`
              )} ${currency}`}
              /{t(`${translations.common}:units:${productUnit}`)}
            </InputLabel>
          </Col>
        ) : null}
        <Col>
          <InputLabel>
            {t(
              `${translations.calculationWindow}:average-hourly-earnings-short`
            )}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Total hours`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Installers`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Work days`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Weeks`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Months`)}
          </InputLabel>
        </Col>
        {showPerUnit ? (
          <Col>
            <InputLabel>
              {t(`${translations.common}:units:${productUnit}`)}/
              {t(`${translations.common}:units:${H}`)}
            </InputLabel>
          </Col>
        ) : null}
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} ${currency}`}/
            {t(`${translations.common}:units:${H}`)}
          </InputLabel>
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="intallations-expense-sum"
            unit={currency}
            value={formatGroupedNumber(
              installationContract.installationsExpenseTotal
            )}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="installation-contract-eur"
            unit={currency}
            value={formatGroupedNumber(
              installationContract.installationsInstallationPriceSum
            )}
          />
        </Col>
        {showPerUnit ? (
          <Col>
            <NumberWithUnitInputField
              disabled
              id="installation-contract-eur-for-unit"
              unit={`${currency}/${t(
                `${translations.common}:units:${productUnit}`
              )}`}
              value={formatGroupedNumber(
                installationContract.installationsUnitInstallationPrice
              )}
            />
          </Col>
        ) : null}
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            id="average-hourly-earnings"
            name="averageHourlyEarnings"
            onChange={handleAverageHourlyEarnings}
            unit={currency}
            value={version.averageHourlyEarnings}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="total-hours"
            unit={H}
            value={formatGroupedNumber(installationContract.totalHours)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            id="installers"
            name="installers"
            onChange={handleInstallers}
            unit={t(`${translations.calculationWindow}:installers-short`)}
            value={installers}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            id="work-days"
            name="workDays"
            onChange={getTimeHandler('days')}
            unit={t(`${translations.calculationWindow}:work-days-short`)}
            value={getTimeValue('days', shownWorkDays)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            id="work-weeks"
            name="workWeeks"
            onChange={getTimeHandler('weeks')}
            unit={t(`${translations.calculationWindow}:weeks-short`)}
            value={getTimeValue('weeks', getWorkWeeks(shownWorkDays))}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            id="work-months"
            name="workMonths"
            onChange={getTimeHandler('months')}
            unit={t(`${translations.calculationWindow}:months-short`)}
            value={getTimeValue('months', getWorkMonths(shownWorkDays))}
          />
        </Col>
        {showPerUnit ? (
          <Col>
            <NumberWithUnitInputField
              disabled
              id="unit-for-h"
              unit={t(`${translations.common}:units:${productUnit}`)}
              value={formatGroupedNumber(installationContract.unitForH)}
            />
          </Col>
        ) : null}
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="contribution-margin-eur-for-h"
            unit={currency}
            value={formatGroupedNumber(
              installationContract.contributionMarginEurForH
            )}
          />
        </Col>
      </Row>
    </div>
  );
};

export default InstallationContract;
